import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { ArchivedConversionsGrid, ArchivedConversionsColumnFilters } from './ArchivedConversionsGrid';
import { ConversionReportArchiveClient, ConversionReportArchiveSummaryModel } from 'client/ApiClient';

const emptyColumnFilters: ArchivedConversionsColumnFilters = {
    conversionName: '',
    conversionType: '',
    clientName: '',
    runType: '',
    createdByName: '',
    goLiveDate: '',
};

interface ArchivedConversionReportsPageState {
    records: ConversionReportArchiveSummaryModel[];
    isLoading: boolean;
    error: string | null;
    searchText: string;
    showFilters: boolean;
    columnFilters: ArchivedConversionsColumnFilters;
}

class ArchivedConversionReportsPage extends React.Component<RouteComponentProps, ArchivedConversionReportsPageState> {
    private readonly client = new ConversionReportArchiveClient();

    constructor(props: RouteComponentProps) {
        super(props);
        this.state = {
            records: [],
            isLoading: true,
            error: null,
            searchText: '',
            showFilters: false,
            columnFilters: { ...emptyColumnFilters },
        };
    }

    componentDidMount() {
        this.loadArchivedConversions();
    }

    private loadArchivedConversions = async () => {
        try {
            this.setState({ isLoading: true, error: null });
            const records = await this.client.getArchivedConversions();
            this.setState({ records: records || [], isLoading: false });
        } catch (err: any) {
            const status = err?.status;
            const message = status === 401 || status === 403
                ? 'Your session has expired. Please log in again to view this report.'
                : 'Failed to load archived conversion reports. Please try again.';
            this.setState({ isLoading: false, error: message });
        }
    };

    private handleSearchChange = (searchText: string) => {
        this.setState({ searchText });
    };

    private handleColumnFilterChange = (column: keyof ArchivedConversionsColumnFilters, value: string) => {
        this.setState(prev => ({ columnFilters: { ...prev.columnFilters, [column]: value } }));
    };

    private handleToggleFilters = () => {
        this.setState(prev => {
            const showFilters = !prev.showFilters;
            return {
                showFilters,
                // Clear any per-column filter values whenever the filter icons are hidden.
                columnFilters: showFilters ? prev.columnFilters : { ...emptyColumnFilters },
            };
        });
    };

    private handleClearFilters = () => {
        this.setState({ searchText: '', columnFilters: { ...emptyColumnFilters } });
    };

    public render() {
        const { records, isLoading, error, searchText, showFilters, columnFilters } = this.state;

        return (
            <>
                {isLoading && <p>Loading archived conversion reports...</p>}
                {error && <Alert color="danger">{error}</Alert>}
                {!isLoading && !error && (
                    <ArchivedConversionsGrid
                        records={records}
                        searchText={searchText}
                        showFilters={showFilters}
                        columnFilters={columnFilters}
                        onSearchChange={this.handleSearchChange}
                        onToggleFilters={this.handleToggleFilters}
                        onColumnFilterChange={this.handleColumnFilterChange}
                        onClearFilters={this.handleClearFilters}
                        onRowClick={id => {
                            const record = records.find(r => r.id === id);
                            this.props.history.push({
                                pathname: `/archived-reports/reports/${id}`,
                                state: { conversionName: record?.conversionName, conversionType: record?.conversionType },
                            });
                        }}
                    />
                )}
            </>
        );
    }
}

export default withRouter(ArchivedConversionReportsPage);
